/**
 * TeacherMyRequests (BON-242) — the teacher's own raised hands.
 *
 * "Waiting on approval" holds only requests an RD can still act on. A shift
 * that was withdrawn, whose workshop was cancelled, or whose date has passed
 * leaves the request row untouched, so without the server's `stillLive` flag
 * that list would accumulate undecidable rows indefinitely.
 *
 * The design shows four states (pending / confirmed / not selected /
 * withdrawn). Only **pending** and **withdrawn** are reachable in BON-242 —
 * confirmed and not-selected are produced by RD confirmation in BON-243, so
 * rendering them now would be dead code branching on values the API cannot
 * return. They land with the feature that creates them.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  fetchMyRequests,
  withdrawShiftRequest,
  type MyRequestEntry,
} from '../../../api/shiftRequests';
import { crewPositionLabel } from '../../../lib/crewPositions';
import { BucketError, BucketLoading, SectionCard } from '../regional/BucketStates';
import { formatShiftStart } from '../regional/shiftFormat';

export const MY_REQUESTS_KEY = ['me.shiftRequests'];

function RequestRow({
  entry,
  onWithdraw,
  busy,
}: {
  entry: MyRequestEntry;
  onWithdraw: (id: string) => void;
  busy: boolean;
}) {
  const label = crewPositionLabel(entry.position);
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid var(--nb-border)',
        background: 'var(--nb-surface)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 700 }}>
          {entry.venueShort} · {label}
        </span>
        <span style={{ display: 'block', fontSize: 11, color: 'var(--nb-subtle)' }}>
          {formatShiftStart(entry.startsAtIso)} · {entry.workshopTypeName}
          {entry.othersWaiting > 0 && ` · +${entry.othersWaiting} others waiting`}
        </span>
      </span>
      {entry.status === 'pending' && entry.stillLive ? (
        <button
          type="button"
          className="nb-btn nb-btn-ghost"
          disabled={busy}
          onClick={() => onWithdraw(entry.openShiftId)}
        >
          Withdraw
        </button>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--nb-faint)' }}>
          {entry.status === 'withdrawn' ? 'Withdrawn' : 'No longer available'}
        </span>
      )}
    </li>
  );
}

export default function TeacherMyRequests() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({
    queryKey: MY_REQUESTS_KEY,
    queryFn: fetchMyRequests,
    staleTime: 30_000,
  });

  const withdraw = useMutation({
    mutationFn: withdrawShiftRequest,
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MY_REQUESTS_KEY });
      // The board shows the same request state.
      qc.invalidateQueries({ queryKey: ['dashboards.teacher', 'shifts'] });
    },
    onError: (err: Error) => {
      setError(err.message);
      qc.invalidateQueries({ queryKey: MY_REQUESTS_KEY });
    },
  });

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  // A pending request whose shift died is NOT waiting on anyone — grouping it
  // as pending would grow that count forever with undecidable rows.
  const pending = q.data.filter((r) => r.status === 'pending' && r.stillLive);
  const closed = q.data.filter((r) => r.status !== 'pending' || !r.stillLive);

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 640 }}>
      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
          {error}
        </div>
      )}

      <SectionCard title={`Waiting on approval · ${pending.length}`}>
        {pending.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
            {/* "You haven't requested any shifts yet" would contradict the Closed
                card sitting right below whenever one exists. */}
            {closed.length > 0
              ? 'Nothing waiting on your director right now. Your closed requests are below.'
              : 'You haven’t requested any shifts yet. Anything you raise a hand for shows up here until your director confirms it.'}
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
            {pending.map((entry) => (
              <RequestRow
                key={entry.openShiftId}
                entry={entry}
                busy={withdraw.isPending}
                onWithdraw={(id) => withdraw.mutate(id)}
              />
            ))}
          </ul>
        )}
      </SectionCard>

      {closed.length > 0 && (
        <SectionCard title="Closed">
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
            {closed.map((entry) => (
              <RequestRow
                key={entry.openShiftId}
                entry={entry}
                busy={withdraw.isPending}
                onWithdraw={(id) => withdraw.mutate(id)}
              />
            ))}
          </ul>
        </SectionCard>
      )}
    </div>
  );
}
